/**
 * The reflection guidelines: what may be published as a reflection on Otable. Authors agree to them once,
 * before their first published reflection (kept with its date on the backend: ReflectionsPage.rules_accepted_at);
 * every reflection's page carries the disclaimer and a way to report it.
 */
import React from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Link, Typography } from '@mui/material';

export const GUIDELINES = [
  ['Write about your own work and experience.',
    'Share your perspective in your own words. You are responsible for what you publish.'],
  ['Criticise ideas, not people.',
    'No personal attacks (ad hominem), insults or mockery, and no remarks about anyone’s character, appearance, background or private life. Disagree with arguments, methods and results, respectfully.'],
  ['Be fair and accurate.',
    'Do not present as fact what you cannot support. No false or misleading statements about people, institutions, companies or results: they can be defamatory.'],
  ['Respect confidentiality.',
    'Do not disclose other people’s unpublished data or results, the content of peer reviews or grant evaluations, confidential agreements, or anything shared with you in confidence.'],
  ['Protect personal data.',
    'Do not share other people’s personal information (contact details, health, private messages) or make them identifiable without their consent. Never include patient or study-participant data.'],
  ['Respect intellectual property.',
    'Quote briefly and cite your sources. Do not copy text, figures or images you have no right to use.'],
  ['No harassment, hate or discrimination.',
    'No threats, intimidation or content that targets anyone for who they are.'],
  ['No spam or undisclosed promotion.',
    'Do not advertise. When you write about a company, product or funder you are connected to, say so.'],
  ['Keep it lawful.',
    'Nothing illegal, and nothing that helps anyone cause harm.'],
];

export const ENFORCEMENT = 'Otable may hide or remove reflections that break these guidelines and may restrict accounts '
  + 'that break them repeatedly. Anyone signed in can report a reflection; the Otable team reviews every report.';

export const DISCLAIMER = 'Reflections are the views of their authors, not of Otable. Otable does not review reflections '
  + 'before they are published and is not responsible for their content.';

export function GuidelinesList({ dense = false }) {
  return (
    <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
      {GUIDELINES.map(([rule, detail]) => (
        <Box component="li" key={rule} sx={{ mb: dense ? 0.75 : 1.25 }}>
          <Typography variant="body2" sx={{ fontWeight: 700 }}>{rule}</Typography>
          <Typography variant="body2" color="text.secondary">{detail}</Typography>
        </Box>
      ))}
    </Box>
  );
}

export function GuidelinesDialog({ open, onClose }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>Reflection guidelines</DialogTitle>
      <DialogContent dividers>
        <GuidelinesList />
        <Typography variant="body2" sx={{ mt: 2 }}>{ENFORCEMENT}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{DISCLAIMER}</Typography>
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

/** "Reflection guidelines" as a link that opens them. */
export function GuidelinesLink({ children = 'Reflection guidelines', sx }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <Link component="button" type="button" variant="body2" onClick={() => setOpen(true)} sx={sx}>{children}</Link>
      <GuidelinesDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
